import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";

/** Arrow keys move between a radiogroup's options; Enter / Space picks. Only the checked one is a tab stop. */
export function useRovingRadio() {
  const [group, setGroup] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!group) return;
    const radios = [...group.querySelectorAll<HTMLElement>('[role="radio"]')];
    const checked = radios.find((r) => r.getAttribute("aria-checked") === "true") ?? radios[0];
    for (const r of radios) r.tabIndex = r === checked ? 0 : -1;
  });
  return { attach: setGroup, onKeyDown: moveRovingFocus };
}

function moveRovingFocus(e: KeyboardEvent<HTMLDivElement>) {
  const keys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
  if (!keys.includes(e.key)) return;
  const radios = [...e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]:not([disabled])')];
  const at = radios.indexOf(document.activeElement as HTMLElement);
  if (at < 0 || radios.length === 0) return;
  e.preventDefault();
  e.stopPropagation();
  const next =
    e.key === "Home" ? 0 : e.key === "End" ? radios.length - 1 : (at + (e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 1) + radios.length) % radios.length;
  radios[next].focus();
  radios[next].scrollIntoView?.({ block: "nearest", inline: "nearest" });
}

const PREVIEW_HOVER_MS = 500;
const PREVIEW_HOLD_MS = 450;

/**
 * Hover (mouse) or press-and-hold (touch) on an option for a beat: a look,
 * not a pick. Reported as `finish_option_previewed` if it is never picked.
 */
export function usePreview(onPreview: () => void) {
  const timer = useRef<number | null>(null);
  const clear = () => {
    if (timer.current != null) window.clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => clear, []);
  return {
    onPointerEnter: (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      clear();
      timer.current = window.setTimeout(onPreview, PREVIEW_HOVER_MS);
    },
    onPointerDown: (e: PointerEvent) => {
      if (e.pointerType === "mouse") return;
      clear();
      timer.current = window.setTimeout(onPreview, PREVIEW_HOLD_MS);
    },
    onPointerLeave: clear,
    onPointerUp: clear,
    onPointerCancel: clear,
  };
}
